import React, { useState } from 'react';
import styled from 'styled-components';
import { Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';
import { callTypes } from '../assets/data';
import { jsPDF } from 'jspdf';
import 'jspdf-autotable';

// Import Space Grotesk font
import '@fontsource/space-grotesk/300.css';
import '@fontsource/space-grotesk/400.css';
import '@fontsource/space-grotesk/500.css';
import '@fontsource/space-grotesk/600.css';
import '@fontsource/space-grotesk/700.css';

// Add Recoleta font styles
// const RecoletaFont = styled.style`
//   @import url('https://fonts.cdnfonts.com/css/recoleta');
// `;

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

// Define all possible sources
const ALL_SOURCES = ['ONLINE FORM', 'EMAIL', 'SNAIL MAIL', 'TELEMINISTRY', 'OTHER', 'WEPRAY', 'VOICE MAIL'];

const Reports = ({
  prayerRequests,
  showCurrentMonth: showCurrentMonthProp,
  onShowCurrentMonthChange,
  startDate: startDateProp,
  endDate: endDateProp,
  onStartDateChange,
  onEndDateChange,
  onClearDateFilters,
}) => {
  const [showCurrentMonthLocal, setShowCurrentMonthLocal] = useState(false);
  const [startDateLocal, setStartDateLocal] = useState('');
  const [endDateLocal, setEndDateLocal] = useState('');
  const [showExportDropdown, setShowExportDropdown] = useState(false);

  const isControlled = onShowCurrentMonthChange != null;
  const showCurrentMonth = isControlled ? (showCurrentMonthProp ?? false) : showCurrentMonthLocal;
  const startDate = isControlled ? (startDateProp ?? '') : startDateLocal;
  const endDate = isControlled ? (endDateProp ?? '') : endDateLocal;
  const setShowCurrentMonth = isControlled ? onShowCurrentMonthChange : setShowCurrentMonthLocal;
  const setStartDate = isControlled ? (onStartDateChange || (() => {})) : setStartDateLocal;
  const setEndDate = isControlled ? (onEndDateChange || (() => {})) : setEndDateLocal;

  // Filter out deleted requests
  const activeRequests = prayerRequests.filter(req => {
    const isNotDeleted = req.status !== 'deleted';
    
    if (showCurrentMonth) {
      // Ensure proper date parsing by handling both ISO string and timestamp formats
      const requestDate = req.created_at instanceof Date ? req.created_at : new Date(req.created_at);
      const now = new Date();
      
      // Check if the date is valid before comparing
      if (isNaN(requestDate.getTime())) {
        console.warn('Invalid date found:', req.created_at);
        return false;
      }
      
      const isCurrentMonth = requestDate.getMonth() === now.getMonth() && 
                           requestDate.getFullYear() === now.getFullYear();
      return isNotDeleted && isCurrentMonth;
    } else {
      // For "All Time" with date range filter
      if (startDate || endDate) {
        const requestDate = req.created_at instanceof Date ? req.created_at : new Date(req.created_at);
        
        // Check if the date is valid before comparing
        if (isNaN(requestDate.getTime())) {
          console.warn('Invalid date found:', req.created_at);
          return false;
        }
        
        const requestDateOnly = new Date(requestDate.getFullYear(), requestDate.getMonth(), requestDate.getDate());
        
        if (startDate && endDate) {
          const start = new Date(startDate);
          const end = new Date(endDate);
          return isNotDeleted && requestDateOnly >= start && requestDateOnly <= end;
        } else if (startDate) {
          const start = new Date(startDate);
          return isNotDeleted && requestDateOnly >= start;
        } else if (endDate) {
          const end = new Date(endDate);
          return isNotDeleted && requestDateOnly <= end;
        }
      }
      return isNotDeleted;
    }
  });

  // Filter processed requests based on processed_at date
  const processedRequests = prayerRequests.filter(req => {
    const isNotDeleted = req.status !== 'deleted';
    const isProcessed = req.status === 'processed' || req.status === 'prayed' || req.status === 'completed' || req.status === 'pending';
    
    if (!isProcessed) return false;
    
    if (showCurrentMonth) {
      // For current month, check if processed in current month
      const processedDate = req.processed_at instanceof Date ? req.processed_at : new Date(req.processed_at);
      const now = new Date();
      
      // Check if the date is valid before comparing
      if (isNaN(processedDate.getTime())) {
        console.warn('Invalid processed date found:', req.processed_at);
        return false;
      }
      
      const isCurrentMonth = processedDate.getMonth() === now.getMonth() && 
                           processedDate.getFullYear() === now.getFullYear();
      return isNotDeleted && isCurrentMonth;
    } else {
      // For "All Time" with date range filter, check processed_at date
      if (startDate || endDate) {
        const processedDate = req.processed_at instanceof Date ? req.processed_at : new Date(req.processed_at);
        
        // Check if the date is valid before comparing
        if (isNaN(processedDate.getTime())) {
          console.warn('Invalid processed date found:', req.processed_at);
          return false;
        }
        
        const processedDateOnly = new Date(processedDate.getFullYear(), processedDate.getMonth(), processedDate.getDate());
        
        if (startDate && endDate) {
          const start = new Date(startDate);
          const end = new Date(endDate);
          return isNotDeleted && processedDateOnly >= start && processedDateOnly <= end;
        } else if (startDate) {
          const start = new Date(startDate);
          return isNotDeleted && processedDateOnly >= start;
        } else if (endDate) {
          const end = new Date(endDate);
          return isNotDeleted && processedDateOnly <= end;
        }
      }
      return isNotDeleted;
    }
  });

  // Calculate total active requests
  const totalActiveRequests = activeRequests.length;
  
  // Calculate total processed requests
  const totalProcessedRequests = processedRequests.length;

  // Calculate source distribution with all sources
  const sourceCounts = ALL_SOURCES.reduce((acc, source) => {
    acc[source] = activeRequests.filter(req => 
      (req.source ? req.source.replace('_', ' ').toUpperCase() : 'UNSPECIFIED') === source
    ).length;
    return acc;
  }, {});

  // Get all possible categories from callTypes
  const allCategories = callTypes.map(type => type.type);

  // Calculate category distribution with all categories
  const categoryCounts = allCategories.reduce((acc, category) => {
    acc[category] = activeRequests.filter(req => 
      (req.category || 'UNCATEGORIZED') === category
    ).length;
    return acc;
  }, {});

  // Define colors for sources
  const sourceColors = {
    'ONLINE FORM': '#2DB3C0',  // Teal
    'EMAIL': '#FF7234',       // Orange
    'SNAIL MAIL': '#EC3310',  // Red
    'TELEMINISTRY': '#00323B', // Dark Blue
    'OTHER': '#6B7280',       // Gray
    'WEPRAY': '#10B981',      // Green
    'VOICE MAIL': '#8B5CF6'   // Purple
  };

  // Define colors for categories
  const categoryColors = {
    'PRAYER REQUEST': '#2DB3C0',  // Teal
    'PRAISE REPORT': '#FF7234',   // Orange
    'TESTIMONY': '#EC3310',       // Red
    'GENERAL INQUIRY': '#00323B', // Dark Blue
    'OTHER': '#6B7280'           // Gray
  };

  // Prepare data for source chart
  const sourceChartData = {
    labels: Object.keys(sourceCounts),
    datasets: [
      {
        label: 'Number of Requests',
        data: Object.values(sourceCounts),
        backgroundColor: Object.keys(sourceCounts).map(source => sourceColors[source]),
        borderColor: Object.keys(sourceCounts).map(source => sourceColors[source]),
        borderWidth: 1,
      },
    ],
  };

  // Prepare data for category chart
  const categoryChartData = {
    labels: Object.keys(categoryCounts),
    datasets: [
      {
        label: 'Number of Requests',
        data: Object.values(categoryCounts),
        backgroundColor: Object.keys(categoryCounts).map(category => categoryColors[category]),
        borderColor: Object.keys(categoryCounts).map(category => categoryColors[category]),
        borderWidth: 1,
      },
    ],
  };

  const chartOptions = {
    responsive: true,
    plugins: {
      legend: {
        position: 'top',
      },
      title: {
        display: true,
        font: {
          size: 16,
          weight: 'bold',
        },
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          stepSize: 1,
        },
      },
    },
  };

  // Export to CSV
  const exportToCSV = () => {
    // Prepare data for CSV
    const headers = ['Source', 'Count'];
    const sourceData = Object.entries(sourceCounts).map(([source, count]) => [source, count]);
    
    const categoryHeaders = ['Category', 'Count'];
    const categoryData = Object.entries(categoryCounts).map(([category, count]) => [category, count]);
    
    // Get date range info
    let dateRangeInfo = showCurrentMonth ? 'Current Month' : 'All Time';
    if (!showCurrentMonth && (startDate || endDate)) {
      if (startDate && endDate) {
        dateRangeInfo = `${startDate} to ${endDate}`;
      } else if (startDate) {
        dateRangeInfo = `From ${startDate}`;
      } else if (endDate) {
        dateRangeInfo = `Until ${endDate}`;
      }
    }
    
    // Combine data
    const csvContent = [
      'Prayer Request Reports',
      dateRangeInfo,
      '',
      `Total Prayer Requests: ${totalActiveRequests}`,
      `Processed Prayer Requests: ${totalProcessedRequests}`,
      '',
      'Requests by Source',
      headers.join(','),
      ...sourceData.map(row => row.join(',')),
      '',
      'Requests by Category',
      categoryHeaders.join(','),
      ...categoryData.map(row => row.join(','))
    ].join('\n');
    
    // Create and download file
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', `prayer_requests_${showCurrentMonth ? 'current_month' : 'date_range'}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Export to PDF
  const exportToPDF = () => {
    const doc = new jsPDF();
    
    // Add title
    doc.setFontSize(18);
    doc.text('Prayer Request Reports', 14, 15);
    
    // Get date range info
    let dateRangeInfo = showCurrentMonth ? 'Current Month' : 'All Time';
    if (!showCurrentMonth && (startDate || endDate)) {
      if (startDate && endDate) {
        dateRangeInfo = `${startDate} to ${endDate}`;
      } else if (startDate) {
        dateRangeInfo = `From ${startDate}`;
      } else if (endDate) {
        dateRangeInfo = `Until ${endDate}`;
      }
    }
    
    // Add date range
    doc.setFontSize(12);
    doc.text(dateRangeInfo, 14, 25);
    
    // Add total counts
    doc.setFontSize(14);
    doc.text(`Total Prayer Requests: ${totalActiveRequests}`, 14, 35);
    doc.text(`Processed Prayer Requests: ${totalProcessedRequests}`, 14, 45);
    
    // Add source data
    doc.setFontSize(16);
    doc.text('Requests by Source', 14, 60);
    
    const sourceTableData = Object.entries(sourceCounts).map(([source, count]) => [source, count]);
    doc.autoTable({
      startY: 65,
      head: [['Source', 'Count']],
      body: sourceTableData,
      theme: 'grid',
      headStyles: { fillColor: [16, 98, 113] }
    });
    
    // Add category data
    const finalY = doc.lastAutoTable.finalY || 65;
    doc.setFontSize(16);
    doc.text('Requests by Category', 14, finalY + 20);
    
    const categoryTableData = Object.entries(categoryCounts).map(([category, count]) => [category, count]);
    doc.autoTable({
      startY: finalY + 25,
      head: [['Category', 'Count']],
      body: categoryTableData,
      theme: 'grid',
      headStyles: { fillColor: [16, 98, 113] }
    });
    
    // Save the PDF
    doc.save(`prayer_requests_${showCurrentMonth ? 'current_month' : 'date_range'}.pdf`);
  };

  // Toggle export dropdown
  const toggleExportDropdown = () => {
    setShowExportDropdown(!showExportDropdown);
  };

  // Clear date filters
  const clearDateFilters = () => {
    if (onClearDateFilters) {
      onClearDateFilters();
    } else {
      setStartDate('');
      setEndDate('');
    }
  };

  return (
    <ReportsContainer>
      <HeaderSection>
        <ControlsRow>
          <ToggleContainer>
            <ToggleButton 
              active={!showCurrentMonth}
              onClick={() => setShowCurrentMonth(false)}
            >
              All Time
            </ToggleButton>
            <ToggleButton 
              active={showCurrentMonth}
              onClick={() => setShowCurrentMonth(true)}
            >
              Current Month
            </ToggleButton>
          </ToggleContainer>
          
          <ExportContainer>
            <ExportButton onClick={toggleExportDropdown}>
              Export
              <ExportIcon>▼</ExportIcon>
            </ExportButton>
            {showExportDropdown && (
              <ExportDropdown>
                <ExportOption onClick={exportToCSV}>Export as CSV</ExportOption>
                <ExportOption onClick={exportToPDF}>Export as PDF</ExportOption>
              </ExportDropdown>
            )}
          </ExportContainer>
        </ControlsRow>

        {/* Date Range Filter - Only show when "All Time" is selected */}
        {!showCurrentMonth && (
          <DateRangeContainer>
            <DateRangeTitle>Filter by Date Range</DateRangeTitle>
            <DateInputsContainer>
              <DateInputGroup>
                <DateLabel>Start Date:</DateLabel>
                <DateInput
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  max={endDate || undefined}
                />
              </DateInputGroup>
              <DateInputGroup>
                <DateLabel>End Date:</DateLabel>
                <DateInput
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  min={startDate || undefined}
                />
              </DateInputGroup>
              {(startDate || endDate) && (
                <ClearButton onClick={clearDateFilters}>
                  Clear Filters
                </ClearButton>
              )}
            </DateInputsContainer>
          </DateRangeContainer>
        )}
        
        <StatsRow>
          <StatCard>
            <StatValue>{totalActiveRequests}</StatValue>
            <StatTitle>Prayer Requests</StatTitle>
          </StatCard>
          
          <StatCard>
            <StatValue>{totalProcessedRequests}</StatValue>
            <StatTitle>Processed Requests</StatTitle>
          </StatCard>
        </StatsRow>
      </HeaderSection>

      <ChartSection>
        <ChartCard>
          <ChartTitle>Requests by Source</ChartTitle>
          <Bar data={sourceChartData} options={chartOptions} />
        </ChartCard>

        <ChartCard>
          <ChartTitle>Requests by Category</ChartTitle>
          <Bar data={categoryChartData} options={chartOptions} />
        </ChartCard>
      </ChartSection>
    </ReportsContainer>
  );
};

const ReportsContainer = styled.div`
  padding: 2rem;
  font-family: 'Space Grotesk', sans-serif;
`;

const HeaderSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  margin-bottom: 2rem;
`;

const ControlsRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const StatsRow = styled.div`
  display: flex;
  gap: 1.5rem;
  margin-top: 1.5rem;
`;

const StatCard = styled.div`
  background: #FFEAE4;
  border-radius: 12px;
  padding: 1.5rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  flex: 1;
  max-width: 300px;
  text-align: center;
`;

const StatTitle = styled.h3`
  color: #FF7234;
  font-size: 1rem;
  margin-top: 0.5rem;
  font-family: 'Space Grotesk', sans-serif;
  font-weight: 500;
`;

const StatValue = styled.div`
  color: #EC3310;
  font-size: 64px;
  font-weight: 600;
  font-family: 'Recoleta', serif;
  line-height: 1;
  margin-bottom: 0.5rem;
`;

const ToggleContainer = styled.div`
  display: flex;
  gap: 0.5rem;
  background: #F3F4F6;
  padding: 0.25rem;
  border-radius: 8px;
`;

const ToggleButton = styled.button`
  padding: 0.5rem 1rem;
  border: none;
  border-radius: 6px;
  background: ${props => props.active ? 'white' : 'transparent'};
  color: ${props => props.active ? '#2DB3C0' : '#6B7280'};
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  box-shadow: ${props => props.active ? '0 1px 3px rgba(0, 0, 0, 0.1)' : 'none'};
  font-family: 'Space Grotesk', sans-serif;

  &:hover {
    background: ${props => props.active ? 'white' : '#E5E7EB'};
  }
`;

const ChartSection = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(400px, 1fr));
  gap: 2rem;
`;

const ChartCard = styled.div`
  background: white;
  border-radius: 12px;
  padding: 1.5rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
`;

const ChartTitle = styled.h3`
  color: #374151;
  font-size: 1.25rem;
  margin-bottom: 1.5rem;
  text-align: center;
  font-family: 'Space Grotesk', sans-serif;
  font-weight: 600;
`;

const ExportContainer = styled.div`
  position: relative;
  display: inline-block;
`;

const ExportButton = styled.button`
  background: white;
  color: #106271;
  border: 1px solid #106271;
  border-radius: 8px;
  padding: 0.5rem 1rem;
  font-family: 'Space Grotesk', sans-serif;
  font-weight: 500;
  font-size: 0.875rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  
  &:hover {
    background: #f8f9fa;
  }
`;

const ExportIcon = styled.span`
  font-size: 0.75rem;
  color: #106271;
`;

const ExportDropdown = styled.div`
  position: absolute;
  top: 100%;
  right: 0;
  background: white;
  border-radius: 8px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  width: 180px;
  z-index: 10;
  margin-top: 0.5rem;
  overflow: hidden;
`;

const ExportOption = styled.div`
  padding: 0.75rem 1rem;
  font-family: 'Space Grotesk', sans-serif;
  font-size: 0.875rem;
  color: #374151;
  cursor: pointer;
  
  &:hover {
    background: #F3F4F6;
  }
`;

const DateRangeContainer = styled.div`
  background: #F9FAFB;
  border-radius: 8px;
  padding: 1rem;
  border: 1px solid #E5E7EB;
`;

const DateRangeTitle = styled.h3`
  color: #374151;
  font-size: 1rem;
  margin-bottom: 1rem;
  font-family: 'Space Grotesk', sans-serif;
  font-weight: 500;
`;

const DateInputsContainer = styled.div`
  display: flex;
  gap: 1rem;
  align-items: flex-end;
  flex-wrap: wrap;
`;

const DateInputGroup = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 150px;
`;

const DateLabel = styled.label`
  color: #374151;
  font-size: 0.875rem;
  margin-bottom: 0.25rem;
  font-family: 'Space Grotesk', sans-serif;
  font-weight: 500;
`;

const DateInput = styled.input`
  padding: 0.5rem;
  border: 1px solid #D1D5DB;
  border-radius: 6px;
  font-family: 'Space Grotesk', sans-serif;
  font-size: 0.875rem;
  
  &:focus {
    outline: none;
    border-color: #2DB3C0;
    box-shadow: 0 0 0 3px rgba(45, 179, 192, 0.1);
  }
`;

const ClearButton = styled.button`
  padding: 0.5rem 1rem;
  border: none;
  border-radius: 6px;
  background: #FF7234;
  color: white;
  font-family: 'Space Grotesk', sans-serif;
  font-weight: 500;
  font-size: 0.875rem;
  cursor: pointer;
  transition: all 0.2s;
  height: fit-content;

  &:hover {
    background: #FF5700;
  }
`;

export default Reports; 
